import { isNull } from 'lodash-es'
import { useCallback } from 'react'
import useWorkspaceCurrency from '@/hooks/use-workspace-currency'
import { useIntl } from '@/i18n/hooks/use-intl'
import { PriceRange } from '@/types/schema/kol-schema'

type UseFormatPrice = () => {
  formatPrice: (
    price: number,
    options?: {
      maximumFractionDigits?: number
    },
  ) => string
  formatPriceRange: (priceRange: PriceRange) => string
}

const useFormatPrice: UseFormatPrice = () => {
  const { formatNumber, formatMessage } = useIntl()
  const workspaceCurrency = useWorkspaceCurrency()

  const formatPrice = useCallback<ReturnType<UseFormatPrice>['formatPrice']>(
    (price, options = {}): string => {
      const { maximumFractionDigits = 0 } = options
      return `${formatNumber(price, {
        maximumFractionDigits,
        style: 'currency',
        currency: workspaceCurrency,
        currencyDisplay: 'narrowSymbol',
      })}`
    },
    [formatNumber, workspaceCurrency],
  )

  const formatPriceRange = useCallback(
    ({ minPrice, maxPrice }: PriceRange): string => {
      if (isNull(maxPrice)) {
        return `${formatMessage(
          { id: 'campaign:accept_price_minimum' },
          {
            minPrice: formatNumber(minPrice, {
              style: 'currency',
              currency: workspaceCurrency,
              currencyDisplay: 'narrowSymbol',
            }),
          },
        )}`
      }

      if (minPrice === maxPrice) {
        return `${formatNumber(minPrice, {
          style: 'currency',
          currency: workspaceCurrency,
          currencyDisplay: 'narrowSymbol',
        })}`
      }

      return `${formatNumber(minPrice, {
        style: 'currency',
        currency: workspaceCurrency,
      })} - ${formatNumber(maxPrice, {
        style: 'currency',
        currency: workspaceCurrency,
        currencyDisplay: 'narrowSymbol',
      })}`
    },
    [formatMessage, formatNumber, workspaceCurrency],
  )

  return {
    formatPrice,
    formatPriceRange,
  }
}

export default useFormatPrice
